import { ApiError } from "@/api/client";
import { createIntegrationToken } from "@/api/integration-tokens";
import { UI } from "@/lib/labels";
import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Field } from "./ui/field";
import { Input } from "./ui/input";
import { Modal } from "./ui/modal";
import { Spinner } from "./ui/spinner";
import { Toggle } from "./ui/toggle";

interface CreateIntegrationTokenModalProps {
  open: boolean;
  onClose: () => void;
  /** Chamado ao sair do passo "token criado" (fechar/Concluir) para recarregar a lista. */
  onCreated: () => void;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    return error.message;
  }
  return "Erro inesperado.";
}

/** Data de amanhã como `YYYY-MM-DD` (mínimo aceito no seletor de expiração). */
function tomorrowISODate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Modal de criação de token em dois passos: (1) formulário com nome e expiração
 * opcional; (2) exibição do segredo em claro — única vez que ele aparece.
 */
export function CreateIntegrationTokenModal({
  open,
  onClose,
  onCreated,
}: CreateIntegrationTokenModalProps) {
  const [name, setName] = useState("");
  const [withExpiration, setWithExpiration] = useState(false);
  const [date, setDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Zera o estado ao fechar (inclusive fechamentos externos ao handleClose).
  useEffect(() => {
    if (!open) {
      setName("");
      setWithExpiration(false);
      setDate("");
      setSubmitting(false);
      setError(null);
      setCreatedToken(null);
      setCopied(false);
    }
  }, [open]);

  const handleClose = () => {
    // Ao sair do passo "criado", recarrega a lista para refletir o novo token.
    const wasCreated = createdToken !== null;
    onClose();
    if (wasCreated) {
      onCreated();
    }
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Dê um nome ao token.");
      return;
    }
    let expiresAt: string | undefined;
    if (withExpiration) {
      if (!date) {
        setError("Escolha uma data de expiração.");
        return;
      }
      const [year, month, day] = date.split("-");
      // Expira no fim do dia local escolhido (valor vem de um <input type="date">).
      expiresAt = new Date(Number(year), Number(month) - 1, Number(day), 23, 59, 59).toISOString();
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await createIntegrationToken(
        expiresAt ? { name: trimmed, expires_at: expiresAt } : { name: trimmed },
      );
      setCreatedToken(res.token);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!createdToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Área de transferência indisponível — o token pode ser copiado manualmente.
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={createdToken ? UI.tokens.createdTitle : UI.tokens.createTitle}
    >
      {createdToken ? (
        <div className="space-y-4">
          <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
            <p className="text-sm font-medium text-amber-800">{UI.tokens.copyWarning}</p>
            <p className="mt-1 text-xs text-amber-700">{UI.tokens.copyHint}</p>
          </div>
          <div className="flex gap-2">
            <Input
              value={createdToken}
              readOnly
              aria-label="Token de integração"
              className="font-mono text-xs"
            />
            <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
              {copied ? (
                <Check className="h-4 w-4 text-primary" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? UI.tokens.copied : UI.tokens.copy}
            </Button>
          </div>
          <div className="flex justify-end">
            <Button type="button" onClick={handleClose}>
              {UI.tokens.done}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <Field label={UI.tokens.nameLabel} htmlFor="token-name" hint={UI.tokens.nameHint}>
            <Input
              id="token-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={UI.tokens.namePlaceholder}
              maxLength={100}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Enter" && !withExpiration) {
                  event.preventDefault();
                  void handleSubmit();
                }
              }}
            />
          </Field>

          <Toggle
            label={UI.tokens.setExpiration}
            checked={withExpiration}
            onChange={(checked) => {
              setWithExpiration(checked);
              if (!checked) {
                setDate("");
              }
            }}
          />

          {withExpiration ? (
            <Field label={UI.tokens.expirationLabel} htmlFor="token-expiration">
              <Input
                id="token-expiration"
                type="date"
                value={date}
                min={tomorrowISODate()}
                onChange={(event) => setDate(event.target.value)}
              />
            </Field>
          ) : null}

          {error ? (
            <p className="text-xs text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              {UI.actions.close}
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Spinner className="h-4 w-4" /> : null}
              {UI.tokens.create}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
